import { z } from "zod/v4";
export const DentalChartV2SchemaVersion = 2;
const identifierSchema = z.string().min(1);
const fdiSchema = z.string().regex(/^[1-8][1-8]$/);
const toothSurfaceSchema = z.enum([
    "occlusal",
    "mesial",
    "distal",
    "lingual",
    "vestibular",
]);
const dentalLocationSchema = z
    .object({
    arch: z.enum(["maxillary", "mandibular"]),
    side: z.enum(["left", "right"]),
    ordinal: z.number().int().min(1).max(8).optional(),
})
    .strict();
const toothSchema = z
    .object({
    id: identifierSchema,
    location: dentalLocationSchema,
    dentition: z.enum(["permanent", "deciduous", "unknown"]),
    fdi: fdiSchema.optional(),
    presence: z.enum(["present", "absent"]).optional(),
    development: z.enum(["bud", "developing", "formed"]).optional(),
    eruption: z.enum(["unerupted", "erupting", "erupted"]).optional(),
})
    .strict();
const dentalDeviceSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("implant"),
        id: identifierSchema,
        location: dentalLocationSchema,
        presence: z.enum(["present", "absent"]).optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("crown"),
        id: identifierSchema,
        supportId: identifierSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("restoration"),
        id: identifierSchema,
        supportId: identifierSchema,
        surfaces: z.array(toothSurfaceSchema).min(1),
    })
        .strict(),
]);
const observationSchema = z
    .object({
    id: identifierSchema,
    targetId: identifierSchema,
    definitionId: identifierSchema,
    surfaces: z.array(toothSurfaceSchema).optional(),
})
    .strict();
const annotationSchema = z
    .object({
    id: identifierSchema,
    targetId: identifierSchema.optional(),
    text: z.string(),
})
    .strict();
const graphicMarkSchema = z
    .object({
    id: identifierSchema,
    targetId: identifierSchema,
    kind: z.literal("unclassified"),
    component: z.enum([
        "tooth",
        "occlusal",
        "mesial",
        "distal",
        "lingual",
        "vestibular",
        "apex",
        "canal",
        "post",
        "core",
        "crown",
        "implant",
        "replacement",
        "unknown",
    ]),
    label: z.string().min(1).optional(),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
})
    .strict();
export const DentalChartV2DocumentSchema = z
    .object({
    schemaVersion: z.literal(DentalChartV2SchemaVersion),
    teeth: z.array(toothSchema),
    devices: z.array(dentalDeviceSchema).optional(),
    observations: z.array(observationSchema).optional(),
    annotations: z.array(annotationSchema).optional(),
    graphicMarks: z.array(graphicMarkSchema).optional(),
})
    .strict()
    .superRefine((document, context) => {
    const entityKinds = new Map();
    const identifiers = new Set();
    const addIdentifier = (id, path) => {
        if (identifiers.has(id)) {
            context.addIssue({
                code: "custom",
                message: `Duplicate identifier ${id}`,
                path,
            });
        }
        identifiers.add(id);
    };
    const validateSurfaces = (id, surfaces, path) => {
        if (surfaces && new Set(surfaces).size !== surfaces.length) {
            context.addIssue({
                code: "custom",
                message: `${id} contains duplicate surfaces`,
                path,
            });
        }
    };
    document.teeth.forEach((tooth, index) => {
        addIdentifier(tooth.id, ["teeth", index, "id"]);
        entityKinds.set(tooth.id, "tooth");
        if (tooth.dentition === "deciduous" &&
            tooth.location.ordinal !== undefined &&
            tooth.location.ordinal > 5) {
            context.addIssue({
                code: "custom",
                message: "A deciduous tooth location must have an ordinal from 1 to 5",
                path: ["teeth", index, "location", "ordinal"],
            });
        }
        if (tooth.fdi) {
            const quadrant = Number(tooth.fdi[0]);
            const ordinal = Number(tooth.fdi[1]);
            const validDentition = (tooth.dentition === "permanent" && quadrant >= 1 && quadrant <= 4) ||
                (tooth.dentition === "deciduous" &&
                    quadrant >= 5 &&
                    quadrant <= 8 &&
                    ordinal <= 5);
            if (!validDentition) {
                context.addIssue({
                    code: "custom",
                    message: "FDI does not match the dentition",
                    path: ["teeth", index, "fdi"],
                });
            }
            const expectedLocation = fdiLocation(quadrant);
            if (ordinal !== tooth.location.ordinal ||
                !expectedLocation ||
                expectedLocation.arch !== tooth.location.arch ||
                expectedLocation.side !== tooth.location.side) {
                context.addIssue({
                    code: "custom",
                    message: "FDI does not match the location",
                    path: ["teeth", index, "fdi"],
                });
            }
        }
    });
    document.devices?.forEach((device, index) => {
        addIdentifier(device.id, ["devices", index, "id"]);
        entityKinds.set(device.id, "device");
        if (device.kind === "implant" && device.location.ordinal === undefined) {
            context.addIssue({
                code: "custom",
                message: "An implant must have an exact location",
                path: ["devices", index, "location", "ordinal"],
            });
        }
    });
    document.observations?.forEach((observation, index) => {
        addIdentifier(observation.id, ["observations", index, "id"]);
    });
    document.annotations?.forEach((annotation, index) => {
        addIdentifier(annotation.id, ["annotations", index, "id"]);
    });
    document.graphicMarks?.forEach((mark, index) => {
        addIdentifier(mark.id, ["graphicMarks", index, "id"]);
    });
    document.devices?.forEach((device, index) => {
        if (device.kind === "implant") {
            return;
        }
        if (!entityKinds.has(device.supportId)) {
            context.addIssue({
                code: "custom",
                message: `Unknown support ${device.supportId}`,
                path: ["devices", index, "supportId"],
            });
        }
        if (device.kind === "restoration") {
            if (entityKinds.get(device.supportId) !== "tooth") {
                context.addIssue({
                    code: "custom",
                    message: "A restoration must be supported by a tooth",
                    path: ["devices", index, "supportId"],
                });
            }
            validateSurfaces(device.id, device.surfaces, [
                "devices",
                index,
                "surfaces",
            ]);
        }
        if (device.kind === "crown" &&
            entityKinds.get(device.supportId) !== "tooth" &&
            !(document.devices ?? []).some((candidate) => candidate.kind === "implant" && candidate.id === device.supportId)) {
            context.addIssue({
                code: "custom",
                message: "A crown must be supported by a tooth or implant",
                path: ["devices", index, "supportId"],
            });
        }
    });
    document.observations?.forEach((observation, index) => {
        validateTarget(entityKinds, observation.targetId, observation.id, "observation", ["observations", index, "targetId"], context);
        validateSurfaces(observation.id, observation.surfaces, [
            "observations",
            index,
            "surfaces",
        ]);
    });
    document.annotations?.forEach((annotation, index) => {
        if (annotation.targetId) {
            validateTarget(entityKinds, annotation.targetId, annotation.id, "annotation", ["annotations", index, "targetId"], context);
        }
    });
    document.graphicMarks?.forEach((mark, index) => {
        validateTarget(entityKinds, mark.targetId, mark.id, "graphic mark", ["graphicMarks", index, "targetId"], context);
    });
});
const fdiLocation = (quadrant) => {
    if (quadrant === 1 || quadrant === 5) {
        return { arch: "maxillary", side: "right" };
    }
    if (quadrant === 2 || quadrant === 6) {
        return { arch: "maxillary", side: "left" };
    }
    if (quadrant === 3 || quadrant === 7) {
        return { arch: "mandibular", side: "left" };
    }
    if (quadrant === 4 || quadrant === 8) {
        return { arch: "mandibular", side: "right" };
    }
    return null;
};
const validateTarget = (entityKinds, targetId, id, collection, path, context) => {
    if (!entityKinds.has(targetId)) {
        context.addIssue({
            code: "custom",
            message: `${collection} ${id} references unknown target ${targetId}`,
            path,
        });
    }
};
//# sourceMappingURL=DentalChartV2Schemas.js.map